import React from "react";
import Modal from "./Modal";
import styles from "./CaseStudiesModal.module.scss";

export default function CaseStudiesModal({ isOpen, onClose }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Case Study">
      <div className={styles.caseStudyContainer}>
        <h2 className={styles.mainHeading}>
          When a Busy Salon Still Leaves the Owner Disappointed at Month-End
        </h2>

        <p className={styles.paragraph}>Many salon owners know this feeling.</p>
        <p className={styles.paragraph}>
          The salon stays busy.<br />
          Clients keep coming.<br />
          Staff keeps working.<br />
          The chairs stay occupied.<br />
          From the outside, everything looks active.
        </p>

        <p className={styles.paragraph}>And because everything looks active, the owner also keeps feeling:</p>

        <div className={styles.quoteBox}>
          “This month should end well.”
        </div>

        <p className={styles.paragraph}>
          But then month-end comes.<br />
          The owner sits down.<br />
          He looks at the sales.<br />
          He looks at the expenses.<br />
          He tries to understand what actually remained.
        </p>

        <p className={styles.paragraph}>And then one thought quietly hits him:</p>

        <div className={styles.quoteBox}>
          “The salon stayed busy all month… then why does the final profit still feel so low?”
        </div>

        <p className={styles.paragraph}>
          That moment is painful.<br />
          Because the problem is not that the salon looked weak.<br />
          <span className={styles.highlightText}>The problem is that the salon looked busy, but the month still did not feel rewarding.</span>
        </p>

        <h3 className={styles.subHeading}>Anonymised Salon Example</h3>

        <p className={styles.paragraph}>
          <span className={styles.highlightText}>All month, the salon looked full of work. At month-end, the owner still did not feel satisfied.</span>
        </p>

        <p className={styles.paragraph}>
          This salon was active through the month. Customers were coming in, services were happening, staff was occupied, and the place looked properly running. Anyone looking from outside would feel that the month should end on a decent note.
        </p>

        <p className={styles.paragraph}>
          But when the owner finally sat down at month-end and saw what had actually remained, the feeling inside was not comfort.<br />
          It was disappointment.
        </p>

        <p className={styles.paragraph}>
          The month had looked busy.<br />
          The effort had been real.<br />
          The salon had kept moving.<br />
          But the profit that finally stayed in hand still did not feel equal to that effort.
        </p>

        <p className={styles.paragraph}>
          And that is the kind of pain that is difficult to explain to anyone else.<br />
          Because from outside, people only see a busy salon.<br />
          Only the owner feels what the month actually gave.
        </p>

        <p className={styles.paragraph}>
          <span className={styles.highlightText}>The real pain was not only low profit. The real pain was seeing the truth after it was already too late.</span>
        </p>

        <p className={styles.paragraph}>
          If the owner had known earlier that the month was not shaping up the way it should, he could have done something.
        </p>

        <ul className={styles.bulletList}>
          <li>He could have watched the weak areas more carefully.</li>
          <li>He could have controlled certain costs earlier.</li>
          <li>He could have become alert before too much time had gone.</li>
          <li>He could have responded while the month was still running.</li>
        </ul>

        <p className={styles.paragraph}>
          But that understanding did not come in time.<br />
          It came at month-end.<br />
          And month-end truth always comes late.
        </p>

        <p className={styles.paragraph}>
          By then, the weak days are already over.<br />
          The unnecessary expense is already done.<br />
          The leakage has already happened.<br />
          The chance to control has already passed.
        </p>

        <p className={styles.paragraph}>
          That is why the owner does not only feel disappointed.<br />
          He also feels this:
        </p>

        <div className={styles.quoteBox}>
          “If I had known this in time, I could have handled it.”
        </div>

        <p className={styles.paragraph}>
          That thought stays with him.<br />
          Then the next month begins, with fresh intention.<br />
          Like every serious owner, he naturally tells himself:
        </p>

        <div className={styles.thoughtBox}>
          <p>This month I will keep better control.</p>
          <p>This month I will stay more alert.</p>
          <p>This month I will not let the same thing happen again.</p>
        </div>

        <p className={styles.paragraph}>
          And in the beginning, that intention feels real.<br />
          But salon life does not slow down.
        </p>

        <p className={styles.paragraph}>
          Customers need attention.<br />
          Staff needs handling.<br />
          Services keep moving.<br />
          Calls keep coming.<br />
          Purchases happen.<br />
          Cash moves.<br />
          Small expenses happen.<br />
          Daily salon chores take over.
        </p>

        <p className={styles.paragraph}>
          And while the owner stays busy managing all this, the salon’s real performance still does not stay clearly visible side by side.
        </p>

        <p className={styles.paragraph}>
          So the month keeps moving.<br />
          The owner keeps working.<br />
          The salon keeps looking active.<br />
          And once again, the full truth arrives only at the end.
        </p>

        <div className={styles.cycleBox}>
          <span>Busy month.</span>
          <span>Late understanding.</span>
          <span>No timely control.</span>
          <span>Same month-end feeling.</span>
        </div>

        <p className={styles.paragraph}>
          This is the cycle that quietly drains many salon owners.<br />
          Not because they are not working hard.<br />
          But because the real picture keeps reaching them too late.
        </p>

        <h3 className={styles.subHeading}>The Shift</h3>

        <p className={styles.paragraph}>
          <span className={styles.highlightText}>Munafa Engine did not first change the salon. It first changed when the owner got to know the truth.</span>
        </p>

        <p className={styles.paragraph}>
          That was the real shift.<br />
          Once the salon’s performance started becoming visible week by week, the owner no longer had to wait till month-end to understand what had been happening.
        </p>

        <p className={styles.paragraph}>
          Now, the previous week itself started speaking.<br />
          The owner could start seeing:
        </p>

        <ul className={styles.bulletList}>
          <li>what the salon had actually brought in</li>
          <li>what had gone out</li>
          <li>what had really remained</li>
          <li>where pressure was building</li>
          <li>where money may be slipping</li>
          <li>and whether the week had actually gone well, or had only looked busy from the outside</li>
        </ul>

        <p className={styles.paragraph}>
          This changed something important.<br />
          Earlier, the salon used to stay busy while the truth stayed hidden.<br />
          Now, while the salon was still running, the truth also started coming into view.
        </p>

        <p className={styles.paragraph}>
          And that changed how the owner entered the next week.<br />
          For the first time, the owner got a chance to respond before the month had already slipped away.
        </p>

        <p className={styles.paragraph}>
          If a week had gone weak, it could now be seen in time.<br />
          If a cost area was becoming heavy, it could now be noticed earlier.<br />
          If the salon looked active but the real weekly position was weak, that gap could now be seen before month-end.
        </p>

        <p className={styles.paragraph}>
          This did not mean every problem disappeared overnight.<br />
          But it gave the owner something he had been missing for a long time: <strong>time to react.</strong>
        </p>

        <p className={styles.paragraph}>
          Not after the month was already over.<br />
          Not after the money had already slipped.<br />
          Not after the weak pattern had already repeated.<br />
          But while there was still time to do something.
        </p>

        <p className={styles.paragraph}>
          That is where control started coming back.<br />
          And then month-end started feeling different.
        </p>

        <p className={styles.paragraph}>
          Earlier, month-end used to feel like late bad news.<br />
          Now, month-end started feeling more understood.<br />
          Because the owner was no longer reaching it blindly.
        </p>

        <p className={styles.paragraph}>
          Week by week, the salon’s real position had started becoming visible.<br />
          Week by week, weak signals had started appearing earlier.<br />
          Week by week, attention had started happening at the right time.
        </p>

        <p className={styles.paragraph}>
          And because of that, month-end no longer carried the same helpless feeling.
        </p>

        <p className={styles.paragraph}>
          Earlier, the owner would reach month-end and feel:
        </p>
        <div className={styles.quoteBox}>
          “The month is over, and now I am understanding what went wrong.”
        </div>

        <p className={styles.paragraph}>
          Now, the feeling started changing to:
        </p>
        <div className={styles.quoteBox}>
          “At least this time, I was not completely in the dark.”
        </div>

        <div className={styles.conclusion}>
          <p className={styles.paragraph}>
            That shift matters deeply.<br />
            Because when a salon owner stops running the business blindly, even the month-end starts feeling less painful and more in control.
          </p>

          <p className={styles.paragraph}>
            And when the right things start becoming visible at the right time, the salon can start moving closer to the kind of result the owner had actually been expecting from it.
          </p>

          <p className={styles.paragraph}>
            <span className={styles.highlightText}>Your salon may be going through the same cycle.</span><br />
            A salon can stay busy all month and still leave the owner disappointed at month-end.<br />
            Not because the owner is not working hard.<br />
            Not because the team is not working.<br />
            Not because the salon is empty.<br />
            But because the real truth is becoming visible too late.
          </p>

          <div className={styles.finalImpact}>
            Munafa Engine helps bring that truth forward, from month-end to week-end, so the owner gets a chance to understand earlier, act earlier, and stay more in control while there is still time.
          </div>
        </div>
      </div>
    </Modal>
  );
}
